import * as React from "react";
import { WORKFORCE_PERSONAS, AgentRoutine, PersonaConfig } from "../../../../../convex/aiWorkforce/config";
import { getPersonaTheme } from "./personaTheme";
import { AgentRoutinesModal } from "./AgentRoutinesModal";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Repeat,
  Search,
  Clock,
  Calendar,
  Bot,
  MessageSquare,
  Sparkles,
  CheckCircle2,
  AlertCircle,
  Play,
  Plus,
} from "lucide-react";

interface AllRoutinesModalProps {
  isOpen: boolean;
  onClose: () => void;
  onOpenAgentChat?: (personaKey: string) => void;
}

export function AllRoutinesModal({
  isOpen,
  onClose,
  onOpenAgentChat,
}: AllRoutinesModalProps) {
  const [searchQuery, setSearchQuery] = React.useState("");
  const [selectedPersonaFilter, setSelectedPersonaFilter] = React.useState<string>("all");
  const [isAgentPickerOpen, setIsAgentPickerOpen] = React.useState(false);
  const [creatingForAgent, setCreatingForAgent] = React.useState<string | null>(null);

  // Load routines toggle state from localStorage
  const [routinesState, setRoutinesState] = React.useState<Record<string, boolean>>(() => {
    const saved = localStorage.getItem("mc_agent_routines_state");
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {}
    }
    return {};
  });

  // Schedule overrides for routines
  const [scheduleOverrides, setScheduleOverrides] = React.useState<Record<string, string>>(() => {
    const saved = localStorage.getItem("mc_routine_schedule_overrides");
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {}
    }
    return {};
  });

  // Re-sync when opened
  React.useEffect(() => {
    if (isOpen) {
      const saved = localStorage.getItem("mc_agent_routines_state");
      if (saved) {
        try {
          setRoutinesState(JSON.parse(saved));
        } catch (e) {}
      }
      const savedSched = localStorage.getItem("mc_routine_schedule_overrides");
      if (savedSched) {
        try {
          setScheduleOverrides(JSON.parse(savedSched));
        } catch (e) {}
      }
    }
  }, [isOpen]);

  const handleToggle = (id: string, defaultVal = false) => {
    setRoutinesState((prev) => {
      const isCurrentlyEnabled = prev[id] !== undefined ? prev[id] : defaultVal;
      const next = { ...prev, [id]: !isCurrentlyEnabled };
      localStorage.setItem("mc_agent_routines_state", JSON.stringify(next));
      // Dispatch custom window event so other panes update instantly
      window.dispatchEvent(new Event("storage"));
      return next;
    });
  };

  // Aggregate all routines with their owning persona
  const allPersonas = Object.values(WORKFORCE_PERSONAS);

  const aggregatedRoutines = React.useMemo(() => {
    const list: Array<{ routine: AgentRoutine; persona: PersonaConfig }> = [];

    allPersonas.forEach((persona) => {
      const turnkey = persona.routines || [];
      // Read custom routines from localStorage
      let custom: AgentRoutine[] = [];
      try {
        const customSaved = localStorage.getItem(`mc_custom_routines_${persona.key}`);
        if (customSaved) custom = JSON.parse(customSaved);
      } catch (e) {}

      [...turnkey, ...custom].forEach((routine) => {
        list.push({ routine, persona });
      });
    });

    return list;
  }, [allPersonas, isOpen]);

  const filteredRoutines = React.useMemo(() => {
    return aggregatedRoutines.filter(({ routine, persona }) => {
      if (selectedPersonaFilter !== "all" && persona.key !== selectedPersonaFilter) return false;
      if (searchQuery.trim()) {
        const q = searchQuery.toLowerCase();
        const matchName = routine.name.toLowerCase().includes(q);
        const matchDesc = routine.description.toLowerCase().includes(q);
        const matchPersona = persona.name.toLowerCase().includes(q);
        if (!matchName && !matchDesc && !matchPersona) return false;
      }
      return true;
    });
  }, [aggregatedRoutines, selectedPersonaFilter, searchQuery]);

  const activeCount = aggregatedRoutines.filter(({ routine }) => {
    return routinesState[routine.id] !== undefined
      ? routinesState[routine.id]
      : (routine.defaultEnabled ?? false);
  }).length;

  return (
    <>
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col p-0 gap-0 overflow-hidden bg-white">
        {/* Header */}
        <DialogHeader className="p-6 pb-4 border-b border-gray-100 flex-shrink-0 bg-slate-50/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-indigo-100 text-indigo-700 flex items-center justify-center shadow-xs">
                <Repeat className="w-5 h-5" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <DialogTitle className="text-xl font-bold text-gray-900">
                    All Workforce Standing Routines
                  </DialogTitle>
                  <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800 border border-emerald-200">
                    {activeCount} Active
                  </span>
                </div>
                <DialogDescription className="text-sm text-gray-500 mt-0.5">
                  Automated background recurring checks, shift monitoring, and client follow-ups across all agents.
                </DialogDescription>
              </div>
            </div>

            <Button
              size="sm"
              onClick={() => setIsAgentPickerOpen(true)}
              className="h-9 px-3.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg shadow-xs flex items-center gap-1.5 flex-shrink-0"
            >
              <Plus className="w-4 h-4" />
              <span>Add Routine</span>
            </Button>
          </div>

          {/* Controls: Search and Persona Filter */}
          <div className="mt-4 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Search routines across all agents..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-9 text-sm rounded-lg bg-white"
              />
            </div>

            <div className="flex items-center gap-2 overflow-x-auto pb-1 sm:pb-0">
              <select
                value={selectedPersonaFilter}
                onChange={(e) => setSelectedPersonaFilter(e.target.value)}
                className="h-9 px-3 rounded-lg border border-gray-200 text-xs font-semibold text-gray-700 bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
              >
                <option value="all">All Agents ({aggregatedRoutines.length} Routines)</option>
                {allPersonas.map((p) => {
                  const count = aggregatedRoutines.filter((r) => r.persona.key === p.key).length;
                  return (
                    <option key={p.key} value={p.key}>
                      {p.name} ({count})
                    </option>
                  );
                })}
              </select>
            </div>
          </div>
        </DialogHeader>

        {/* Routines Grid */}
        <div className="flex-1 overflow-y-auto p-6 space-y-3 custom-scrollbar min-h-[300px]">
          {filteredRoutines.length === 0 ? (
            <div className="py-16 flex flex-col items-center justify-center text-center">
              <div className="w-12 h-12 rounded-2xl bg-gray-100 text-gray-400 flex items-center justify-center mb-3">
                <Repeat className="w-6 h-6" />
              </div>
              <h3 className="text-base font-semibold text-gray-800">No routines found</h3>
              <p className="text-sm text-gray-500 max-w-sm mt-1">
                No standing routines matched your search or agent filter.
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3">
              {filteredRoutines.map(({ routine, persona }) => {
                const theme = getPersonaTheme(persona.key);
                const isEnabled =
                  routinesState[routine.id] !== undefined
                    ? routinesState[routine.id]
                    : (routine.defaultEnabled ?? false);
                const currentSchedule = scheduleOverrides[routine.id] || routine.scheduleLabel;

                return (
                  <div
                    key={`${persona.key}-${routine.id}`}
                    className={`p-4 rounded-xl border transition-all bg-white flex flex-col sm:flex-row sm:items-center justify-between gap-4 ${
                      isEnabled
                        ? "border-gray-300 shadow-2xs"
                        : "border-gray-200 opacity-75 hover:opacity-100"
                    }`}
                  >
                    <div className="flex items-start gap-3.5 min-w-0">
                      <div
                        className={`w-10 h-10 rounded-xl ${theme.bgLight} border ${theme.border} ${theme.text} flex items-center justify-center flex-shrink-0 shadow-2xs mt-0.5 sm:mt-0`}
                      >
                        <Bot className="w-5 h-5" />
                      </div>

                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="text-sm font-bold text-gray-900 truncate">
                            {routine.name}
                          </h4>
                          <span className={`text-2xs font-semibold px-2 py-0.5 rounded-full ${theme.badge}`}>
                            {persona.name} ({persona.title.replace(" Agent", "")})
                          </span>
                        </div>

                        <p className="text-xs text-gray-600 mt-1 leading-relaxed line-clamp-2">
                          {routine.description}
                        </p>

                        <div className="flex items-center gap-3 text-xs text-gray-500 mt-2 flex-wrap">
                          <span className="inline-flex items-center gap-1.5 font-medium bg-slate-50 border border-slate-200 px-2 py-0.5 rounded-md">
                            <Clock className="w-3 h-3 text-blue-600" />
                            <span>{currentSchedule}</span>
                          </span>

                          {routine.defaultEnabled && (
                            <span className="text-2xs text-gray-400">Default routine</span>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-3.5 flex-shrink-0 self-end sm:self-center">
                      {onOpenAgentChat && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            onOpenAgentChat(persona.key);
                            onClose();
                          }}
                          className="h-8 text-xs text-blue-600 hover:text-blue-700 hover:bg-blue-50 gap-1"
                        >
                          <MessageSquare className="w-3.5 h-3.5" />
                          <span>Chat Agent</span>
                        </Button>
                      )}

                      <div className="flex items-center gap-2.5 pl-2 border-l border-gray-100">
                        <Switch
                          checked={isEnabled}
                          onCheckedChange={() => handleToggle(routine.id, routine.defaultEnabled)}
                        />
                        <span className={`text-xs font-semibold min-w-[50px] ${isEnabled ? "text-emerald-700" : "text-gray-400"}`}>
                          {isEnabled ? "Active" : "Paused"}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-100 bg-gray-50 flex items-center justify-between text-xs text-gray-500">
          <span>
            {activeCount} of {aggregatedRoutines.length} routines active
          </span>
          <Button variant="outline" size="sm" onClick={onClose}>
            Done
          </Button>
        </div>
      </DialogContent>
    </Dialog>

    {/* Agent Picker Dialog for Add Routine */}
    <Dialog open={isAgentPickerOpen} onOpenChange={setIsAgentPickerOpen}>
      <DialogContent className="max-w-md p-6 bg-white">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold text-gray-900">
            Select Agent for Routine
          </DialogTitle>
          <DialogDescription className="text-sm text-gray-500">
            Choose which specialist you want to assign a new recurring automated routine to.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 pt-2 max-h-[50vh] overflow-y-auto">
          {allPersonas.map((p) => {
            const theme = getPersonaTheme(p.key);
            return (
              <button
                key={p.key}
                onClick={() => {
                  setCreatingForAgent(p.key);
                  setIsAgentPickerOpen(false);
                }}
                className="w-full text-left p-3 rounded-xl border border-gray-200 hover:border-blue-400 hover:bg-blue-50/50 transition-all flex items-center gap-3 group"
              >
                <div
                  className={`w-9 h-9 rounded-lg ${theme.bgLight} border ${theme.border} ${theme.text} flex items-center justify-center flex-shrink-0`}
                >
                  <Bot className="w-4 h-4" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-sm text-gray-900 group-hover:text-blue-700">
                      {p.name}
                    </span>
                    <Badge variant="outline" className="text-2xs font-medium text-gray-500">
                      {p.title.replace(" Agent", "")}
                    </Badge>
                  </div>
                  <p className="text-xs text-gray-500 truncate mt-0.5">{p.description}</p>
                </div>
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>

    {/* Embedded Agent Routines Modal in creation mode */}
    {creatingForAgent && (
      <AgentRoutinesModal
        isOpen={Boolean(creatingForAgent)}
        onClose={() => {
          setCreatingForAgent(null);
          window.dispatchEvent(new Event("storage"));
        }}
        personaKey={creatingForAgent}
        initialCreating={true}
        onOpenChat={onOpenAgentChat}
      />
    )}
  </>
  );
}
